/**
 * Headshot Service
 * Automatically fetches athlete headshots from ESPN when creating clients/prospects
 */

import { SCRAPER_API_URL } from '../lib/config'
import { isCollegeLeague, isEspnCollegeLeague } from './leagueHelpers'

interface HeadshotResult {
  playerName: string
  headshot_url: string | null
  headshotUrl?: string | null
  team?: string
  position?: string
  sport: string
  league: string
  espn_id?: string
  source: string
  found: boolean
}

/**
 * Maps our internal sport/league values to ESPN API format
 */
function mapToESPNLeague(sport?: string | null, league?: string | null): { espnSport: string; espnLeague: string } | null {
  const sportLower = (sport || '').toLowerCase()
  const leagueLower = (league || '').toLowerCase()

  // College is decided FIRST. The old order tested NFL first with an escape
  // hatch that only excluded the literal word "college", so an NCAA or NAIA
  // football player was looked up in the NFL index and could come back
  // wearing a same-named NFL player's face. NAIA and JUCO have no ESPN
  // index at all, so they return null rather than a wrong guess.
  if (isCollegeLeague(league)) {
    if (!isEspnCollegeLeague(league)) return null
    if (leagueLower === 'cfb' || sportLower.includes('football')) return { espnSport: 'cfb', espnLeague: 'cfb' }
    if (leagueLower === 'cbb' || sportLower.includes('basketball')) return { espnSport: 'cbb', espnLeague: 'cbb' }
    return null
  }
  if (leagueLower === 'cfb') return { espnSport: 'cfb', espnLeague: 'cfb' }
  if (leagueLower === 'cbb') return { espnSport: 'cbb', espnLeague: 'cbb' }

  // A named non-ESPN league is not guessed into a pro index by sport alone.
  if (leagueLower.includes('high school') || leagueLower === 'other' || leagueLower === 'international') {
    return null
  }

  // NFL
  if (leagueLower === 'nfl' || (sportLower === 'football' && !league)) {
    return { espnSport: 'nfl', espnLeague: 'nfl' }
  }

  // NBA
  if (leagueLower === 'nba' || (sportLower === 'basketball' && !league)) {
    return { espnSport: 'nba', espnLeague: 'nba' }
  }

  // MLB
  if (leagueLower === 'mlb' || sportLower === 'baseball') {
    return { espnSport: 'mlb', espnLeague: 'mlb' }
  }

  // NHL
  if (leagueLower === 'nhl' || sportLower === 'hockey') {
    return { espnSport: 'nhl', espnLeague: 'nhl' }
  }

  return null
}

/**
 * Checks if we should attempt to fetch a headshot for this entity
 */
export function shouldFetchHeadshot(
  fullName?: string | null,
  sport?: string | null,
  league?: string | null,
  existingHeadshotUrl?: string | null
): boolean {
  // Must have a name
  if (!fullName || fullName.trim().length < 2) {
    return false
  }

  // Already has a headshot
  if (existingHeadshotUrl && existingHeadshotUrl.trim().length > 0) {
    return false
  }

  // Must be a supported sport/league
  const mapping = mapToESPNLeague(sport, league)
  return mapping !== null
}

/**
 * Fetches a headshot URL for an athlete from ESPN
 * Returns null if not found or on error (fails silently)
 */
export async function fetchHeadshot(
  fullName: string,
  sport?: string | null,
  league?: string | null
): Promise<string | null> {
  try {
    const mapping = mapToESPNLeague(sport, league)
    if (!mapping) {
      return null
    }

    const { espnSport } = mapping
    const encodedName = encodeURIComponent(fullName.trim())
    const url = `${SCRAPER_API_URL}/api/stats/headshot/${espnSport}/${encodedName}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      // 10 second timeout
      signal: AbortSignal.timeout(10000),
    })

    if (!response.ok) {
      return null
    }

    const data: HeadshotResult = await response.json()
    const resolvedHeadshot = data.headshot_url || data.headshotUrl || null

    if (data.found && resolvedHeadshot) {
      return resolvedHeadshot
    }

    // Fallback: if ESPN misses, pull any known headshot from unified player search data.
    const fallbackUrl = await fetchHeadshotFromPlayerSearch(fullName, sport)
    if (fallbackUrl) {
      return fallbackUrl
    }

    return null
  } catch (err) {
    // Fail silently - headshot is not critical
    console.warn(`[Headshot] Error fetching for ${fullName}:`, err)
    return null
  }
}

async function fetchHeadshotFromPlayerSearch(
  fullName: string,
  sport?: string | null
): Promise<string | null> {
  try {
    const params = new URLSearchParams({
      q: fullName.trim(),
      limit: '1',
    })
    if (sport) {
      params.set('sport', sport)
    }

    const response = await fetch(`${SCRAPER_API_URL}/api/players/search?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(10000),
    })
    if (!response.ok) return null

    const data = await response.json()
    const player = Array.isArray(data?.players) ? data.players[0] : null
    const url = player?.headshot_url || player?.headshotUrl || null
    return typeof url === 'string' && url.trim().length > 0 ? url : null
  } catch {
    return null
  }
}

/**
 * Attempts to fetch headshot and returns updated payload with headshot_url if found
 * Non-blocking: wraps the fetch in a fire-and-forget update if afterCreate callback is provided
 */
export async function enrichWithHeadshot<T extends { headshot_url?: string | null }>(
  payload: T,
  fullName: string,
  sport?: string | null,
  league?: string | null
): Promise<T> {
  // Check if we should fetch
  if (!shouldFetchHeadshot(fullName, sport, league, payload.headshot_url)) {
    return payload
  }

  // Fetch headshot
  const headshotUrl = await fetchHeadshot(fullName, sport, league)

  if (headshotUrl) {
    return {
      ...payload,
      headshot_url: headshotUrl,
    }
  }

  return payload
}

/**
 * Fire-and-forget headshot fetch that updates the record after creation
 * Use this when you don't want to block client creation
 */
export function fetchHeadshotAsync(
  entityId: string,
  entityType: 'clients' | 'prospects',
  fullName: string,
  sport?: string | null,
  league?: string | null,
  updateFn?: (id: string, data: { headshot_url: string }) => Promise<any>
): void {
  if (!shouldFetchHeadshot(fullName, sport, league, null)) {
    return
  }

  // Fire and forget
  fetchHeadshot(fullName, sport, league).then(async (headshotUrl) => {
    if (headshotUrl && updateFn) {
      try {
        await updateFn(entityId, { headshot_url: headshotUrl })
      } catch (err) {
        console.warn(`[Headshot] Failed to update ${entityType}/${entityId}:`, err)
      }
    }
  }).catch(err => {
    console.warn(`[Headshot] Async fetch failed for ${fullName}:`, err)
  })
}

export interface AthleteLookup {
  headshot_url: string | null
  team: string | null
  position: string | null
  espn_id: string | null
}

/**
 * What ESPN knows about an athlete by name in a given league: club, position,
 * headshot, id. Used when promoting a college client to a pro league, so the
 * club and headshot are ESPN's rather than typed. Throws when the lookup
 * cannot run; resolves to nulls only when ESPN has no such athlete.
 */
export async function lookupAthlete(
  fullName: string,
  sport: string | null | undefined,
  league: string | null | undefined,
): Promise<AthleteLookup> {
  const mapping = mapToESPNLeague(sport, league)
  if (!mapping) throw new Error(`ESPN does not index ${league ?? 'that league'}.`)
  const url = `${SCRAPER_API_URL}/api/stats/headshot/${mapping.espnSport}/${encodeURIComponent(fullName.trim())}`
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10000) })
  if (!response.ok) throw new Error(`ESPN lookup failed (${response.status}).`)
  const data: HeadshotResult & { found?: boolean } = await response.json()
  if (!data.found) return { headshot_url: null, team: null, position: null, espn_id: null }
  return {
    headshot_url: data.headshot_url || data.headshotUrl || null,
    team: data.team ?? null,
    position: data.position ?? null,
    espn_id: data.espn_id ?? null,
  }
}
